import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const escapeXml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const downloadExcel = (fileName, sheetName, headers, rows) => {
  const headerXml = `<Row>${headers
    .map(
      (header) =>
        `<Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(
          header
        )}</Data></Cell>`
    )
    .join("")}</Row>`;

  const bodyXml = rows
    .map(
      (row) =>
        `<Row>${row
          .map(
            (cell) =>
              `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`
          )
          .join("")}</Row>`
    )
    .join("");

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Header">
   <Font ss:Bold="1"/>
   <Interior ss:Color="#E5E7EB" ss:Pattern="Solid"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="${escapeXml(sheetName.slice(0, 31))}">
  <Table>
   ${headerXml}
   ${bodyXml}
  </Table>
 </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], {
    type: "application/vnd.ms-excel",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatYachts = (details) =>
  (Array.isArray(details) ? details : [])
    .map((item) => item.boatName || "")
    .filter(Boolean)
    .join(", ");

const formatSlots = (details) =>
  (Array.isArray(details) ? details : [])
    .map((item) =>
      [item.bookingDate, item.timeSlot].filter(Boolean).join(" ")
    )
    .filter(Boolean)
    .join("; ");

const formatAddons = (addons) =>
  (Array.isArray(addons) ? addons : [])
    .map((item) =>
      item.name
        ? `${item.name}${item.quantity ? ` x${item.quantity}` : ""}`
        : ""
    )
    .filter(Boolean)
    .join(", ");

const ConfirmedBookings = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("indiaboatsAdminToken");
        const response = await fetch(`${API_BASE_URL}/api/bookings`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to load confirmed bookings");
        }

        const result = await response.json();
        setRows(Array.isArray(result) ? result : []);
      } catch (fetchError) {
        console.error("Bookings fetch error:", fetchError);
        setRows([]);
        setError(fetchError.message || "Failed to load confirmed bookings");
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleDownloadExcel = () => {
    if (!rows.length) {
      alert("No confirmed bookings to download.");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Mobile",
      "Yachts",
      "Date & Time",
      "Location",
      "Add-ons",
      "Base Total",
      "Add-ons Total",
      "Tax",
      "Final Amount",
      "Confirmed At",
    ];
    const excelRows = rows.map((row) => [
      row.customerName || "",
      row.email || "",
      row.mobile || "",
      formatYachts(row.bookingDetails),
      formatSlots(row.bookingDetails),
      (row.bookingDetails || [])
        .map((item) => item.location)
        .filter(Boolean)
        .join(", "),
      formatAddons(row.addons),
      row.totalAmount ?? "",
      row.addonTotal ?? "",
      row.taxAmount ?? "",
      row.finalAmount ?? "",
      formatDate(row.createdAt),
    ]);

    downloadExcel("confirmed-bookings", "Confirmed Bookings", headers, excelRows);
  };

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Confirmed Booking</h1>
          <p>
            Bookings confirmed on the website appear here with customer and
            yacht details.
          </p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleDownloadExcel}
          disabled={loading || !rows.length}
        >
          Download Excel
        </button>
      </div>

      <div className="cms-packages-layout">
        <div className="cms-packages-main">
          <div className="cms-section-card">
            <div className="cms-section-card-header">
              <h2>Confirmed Bookings</h2>
              <p>
                {rows.length} {rows.length === 1 ? "booking" : "bookings"} saved
                from the confirm booking page.
              </p>
            </div>

            {loading ? (
              <p className="cms-inquiries-empty">Loading bookings...</p>
            ) : error ? (
              <p className="cms-inquiries-empty">{error}</p>
            ) : rows.length === 0 ? (
              <p className="cms-inquiries-empty">No confirmed bookings yet.</p>
            ) : (
              <div className="cms-enquiries-table-wrap">
                <table className="cms-enquiries-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Yachts</th>
                      <th>Date & Time</th>
                      <th>Add-ons</th>
                      <th>Final Amount</th>
                      <th>Confirmed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{index + 1}</td>
                        <td>{row.customerName}</td>
                        <td>{row.email}</td>
                        <td>{row.mobile}</td>
                        <td>{formatYachts(row.bookingDetails) || "—"}</td>
                        <td>{formatSlots(row.bookingDetails) || "—"}</td>
                        <td>{formatAddons(row.addons) || "—"}</td>
                        <td>
                          {row.finalAmount != null
                            ? `₹ ${Number(row.finalAmount).toFixed(2)}`
                            : "—"}
                        </td>
                        <td>{formatDate(row.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmedBookings;
